import * as THREE from 'three';

export type RaceType = 'circuit' | 'sprint';

/**
 * Advanced Urban Track Definition mit NFS Underground Styling
 */
export interface UrbanTrackDefinition {
  id: string;
  name: string;
  city: string;
  description: string;
  difficulty: 1 | 2 | 3 | 4 | 5;
  length: number;
  avgWidth: number;
  maxLapTime: number;

  raceType: RaceType;
  laps?: number;

  segments: AdvancedSegment[];

  environment: EnvironmentSettings;

  spawnPoint: THREE.Vector3;
  finishPoint: THREE.Vector3;
  checkpoints: CheckpointData[];

  nfsFeatures: NFSUndergroundFeatures;

  // Optional: Externe 3D-Map als Umgebung (GLB)
  environmentMapPath?: string;
  environmentMapScale?: number;
}

export interface AdvancedSegment {
  type: 'straight' | 'left_curve' | 'right_curve' | 'chicane' | 'hairpin' | 'jump' | 'tunnel';
  length: number;
  width: number;

  radius?: number;
  banking?: number;
  elevation?: number;

  surroundings?: SegmentSurroundings;
  lighting?: SegmentLighting;
}

export interface SegmentSurroundings {
  left: 'grass' | 'concrete' | 'sand' | 'water' | 'building' | 'tunnel_wall' | 'cliff';
  right: 'grass' | 'concrete' | 'sand' | 'water' | 'building' | 'tunnel_wall' | 'cliff';
  buildings?: BuildingData[];
  obstacles?: ObstacleData[];
  neonSigns?: NeonSignData[];
}

export interface SegmentLighting {
  streetLights?: boolean;
  neonIntensity?: number;
  fogDensity?: number;
}

export interface BuildingData {
  position: [number, number, number];
  width: number;
  height: number;
  depth: number;
  color: string;
  windows?: boolean;
  lit?: boolean;
}

export interface NeonSignData {
  position: [number, number, number];
  text: string;
  color: string;
  intensity: number;
  animated?: boolean;
}

export interface ObstacleData {
  position: [number, number, number];
  type: 'barrier' | 'cone' | 'crate' | 'car' | 'debris';
  scale?: number;
}

export interface CheckpointData {
  id: string;
  position: THREE.Vector3;
  radius: number;
  name: string;
  type: 'start' | 'checkpoint' | 'finish';
}

export interface EnvironmentSettings {
  timeOfDay: 'night' | 'sunset' | 'day' | 'rainy';
  weather: 'clear' | 'light_rain' | 'heavy_rain' | 'foggy';
  fogColor: number;
  fogDensity: number;
  ambientLight: number;
  streetLightIntensity: number;
  neonGlobalIntensity: number;
}

export interface NFSUndergroundFeatures {
  hasDriftZones: boolean;
  hasSpeedCameras: boolean;
  hasPoliceChase: boolean;
  hasShortcuts: boolean;
  hasJumps: boolean;
  hasTunnels: boolean;
  soundtrackMood: 'aggressive' | 'chill' | 'intense';
}

const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/**
 * Helper: Generate default circuit checkpoints (closed loop)
 * Start, 4 checkpoints, finish at same position as start
 */
export function makeCircuitCheckpoints(): CheckpointData[] {
  return [
    { id: 'start', position: V(0, 1, 0), radius: 25, name: 'START', type: 'start' },
    { id: 'c1', position: V(200, 1, -100), radius: 25, name: 'CP 1', type: 'checkpoint' },
    { id: 'c2', position: V(250, 1, -300), radius: 25, name: 'CP 2', type: 'checkpoint' },
    { id: 'c3', position: V(100, 1, -400), radius: 25, name: 'CP 3', type: 'checkpoint' },
    { id: 'c4', position: V(-100, 1, -250), radius: 25, name: 'CP 4', type: 'checkpoint' },
    { id: 'finish', position: V(0, 1, 0), radius: 30, name: 'FINISH', type: 'finish' },
  ];
}

/**
 * Helper: Generate default sprint checkpoints (A→B point-to-point)
 * Start, 3 checkpoints, finish at different position
 */
export function makeSprintCheckpoints(): CheckpointData[] {
  return [
    { id: 'start', position: V(0, 1, 0), radius: 25, name: 'START', type: 'start' },
    { id: 'c1', position: V(300, 1, -150), radius: 25, name: 'CP 1', type: 'checkpoint' },
    { id: 'c2', position: V(600, 1, -100), radius: 25, name: 'CP 2', type: 'checkpoint' },
    { id: 'c3', position: V(800, 1, -250), radius: 25, name: 'CP 3', type: 'checkpoint' },
    { id: 'finish', position: V(1000, 1, -300), radius: 30, name: 'FINISH', type: 'finish' },
  ];
}

/**
 * Vordefinierte Urban Racing Tracks im NFS Underground Stil
 */
export const URBAN_TRACK_CATALOG: Record<string, UrbanTrackDefinition> = {
  // === EXISTING (kept) ===
  tokyo_night: {
    id: 'tokyo_night',
    name: 'Tokyo Neon Circuit',
    city: 'Tokyo',
    description: 'Neon-Lichter, enge Kurven, klassisches Underground Feeling',
    difficulty: 3,
    length: 4.8,
    avgWidth: 12,
    maxLapTime: 170000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 6, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 180), radius: 7, name: 'CP1', type: 'checkpoint' },
      { id: 'c2', position: V(120, 0.05, 260), radius: 7, name: 'CP2', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 6, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 220, width: 12, lighting: { streetLights: true, neonIntensity: 1.2 } },
      { type: 'right_curve', length: 120, width: 12, radius: 45, lighting: { streetLights: true, neonIntensity: 1.3 } },
      { type: 'straight', length: 220, width: 12, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'left_curve', length: 160, width: 12, radius: 55, lighting: { streetLights: true, neonIntensity: 1.2 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'clear',
      fogColor: 0x06060c,
      fogDensity: 0.018,
      ambientLight: 0.25,
      streetLightIntensity: 1.2,
      neonGlobalIntensity: 1.35
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: false,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: true,
      soundtrackMood: 'intense'
    }
  },

  miami_day: {
    id: 'miami_day',
    name: 'Miami Coast Run',
    city: 'Miami',
    description: 'Schnelle Geraden, breite Straßen, warme Farben',
    difficulty: 2,
    length: 5.2,
    avgWidth: 14,
    maxLapTime: 165000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 6, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 240), radius: 7, name: 'CP1', type: 'checkpoint' },
      { id: 'c2', position: V(-140, 0.05, 360), radius: 7, name: 'CP2', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 6, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 260, width: 14, lighting: { streetLights: false, neonIntensity: 0.3 } },
      { type: 'left_curve', length: 180, width: 14, radius: 70, lighting: { streetLights: false, neonIntensity: 0.35 } },
      { type: 'straight', length: 260, width: 14, lighting: { streetLights: false, neonIntensity: 0.3 } }
    ],
    environment: {
      timeOfDay: 'day',
      weather: 'clear',
      fogColor: 0xa9d3ff,
      fogDensity: 0.004,
      ambientLight: 0.7,
      streetLightIntensity: 0.3,
      neonGlobalIntensity: 0.4
    },
    nfsFeatures: {
      hasDriftZones: false,
      hasSpeedCameras: true,
      hasPoliceChase: false,
      hasShortcuts: true,
      hasJumps: true,
      hasTunnels: false,
      soundtrackMood: 'chill'
    }
  },

  london_rain: {
    id: 'london_rain',
    name: 'London Rainy Alley',
    city: 'London',
    description: 'Regen, Reflexionen, enge Gassen',
    difficulty: 4,
    length: 4.1,
    avgWidth: 10,
    maxLapTime: 175000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 6, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 160), radius: 6, name: 'CP1', type: 'checkpoint' },
      { id: 'c2', position: V(90, 0.05, 250), radius: 6, name: 'CP2', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 6, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 180, width: 10 },
      { type: 'right_curve', length: 110, width: 10, radius: 40 },
      { type: 'straight', length: 160, width: 10 },
      { type: 'left_curve', length: 130, width: 10, radius: 45 }
    ],
    environment: {
      timeOfDay: 'rainy',
      weather: 'heavy_rain',
      fogColor: 0x10131b,
      fogDensity: 0.022,
      ambientLight: 0.35,
      streetLightIntensity: 1.1,
      neonGlobalIntensity: 1.25
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: false,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: true,
      soundtrackMood: 'aggressive'
    }
  },

  // === NEW: Industrial (NFS Underground vibe) ===
  berlin_industrial: {
    id: 'berlin_industrial',
    name: 'Berlin Industrial Night',
    city: 'Berlin',
    description: 'Industrieviertel: Neon, Container, harte Kurven, viel Atmosphäre',
    difficulty: 3,
    length: 5.0,
    avgWidth: 12,
    maxLapTime: 175000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 210), radius: 7, name: 'Container Yard', type: 'checkpoint' },
      { id: 'c2', position: V(-120, 0.05, 330), radius: 7, name: 'Warehouse', type: 'checkpoint' },
      { id: 'c3', position: V(40, 0.05, 430), radius: 7, name: 'Dock Turn', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 7, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 230, width: 12, lighting: { streetLights: true, neonIntensity: 1.3 } },
      { type: 'left_curve', length: 140, width: 12, radius: 55, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'chicane', length: 160, width: 11, lighting: { streetLights: true, neonIntensity: 1.5 } },
      { type: 'right_curve', length: 140, width: 12, radius: 45, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'straight', length: 210, width: 12, lighting: { streetLights: true, neonIntensity: 1.2 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'clear',
      fogColor: 0x05070c,
      fogDensity: 0.02,
      ambientLight: 0.22,
      streetLightIntensity: 1.25,
      neonGlobalIntensity: 1.35
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: true,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: true,
      soundtrackMood: 'intense'
    }
  },

  // === NEW: Harbor / Docks ===
  harbor_docks: {
    id: 'harbor_docks',
    name: 'Harbor Docks Sprint',
    city: 'Hamburg',
    description: 'Docks, Kräne, Wasser, lange Geraden + harte Hairpins',
    difficulty: 2,
    length: 4.6,
    avgWidth: 14,
    maxLapTime: 165000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 260), radius: 7, name: 'Dock Straight', type: 'checkpoint' },
      { id: 'c2', position: V(130, 0.05, 300), radius: 7, name: 'Crane Turn', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 7, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 280, width: 14, lighting: { streetLights: true, neonIntensity: 1.0 } },
      { type: 'hairpin', length: 120, width: 12, radius: 28, lighting: { streetLights: true, neonIntensity: 1.2 } },
      { type: 'straight', length: 260, width: 14, lighting: { streetLights: true, neonIntensity: 1.0 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'light_rain',
      fogColor: 0x070a10,
      fogDensity: 0.018,
      ambientLight: 0.25,
      streetLightIntensity: 1.25,
      neonGlobalIntensity: 1.2
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: false,
      hasPoliceChase: true,
      hasShortcuts: false,
      hasJumps: true,
      hasTunnels: false,
      soundtrackMood: 'aggressive'
    }
  },

  // === NEW: Highway Ring (High speed) ===
  highway_ring: {
    id: 'highway_ring',
    name: 'Highway Ring',
    city: 'Frankfurt',
    description: 'Highspeed Autobahn-Feeling, lange Kurven, klare Linien',
    difficulty: 4,
    length: 6.4,
    avgWidth: 16,
    maxLapTime: 180000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 8, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 320), radius: 8, name: 'Highway 1', type: 'checkpoint' },
      { id: 'c2', position: V(160, 0.05, 520), radius: 8, name: 'Highway 2', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 8, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 340, width: 16, lighting: { streetLights: true, neonIntensity: 0.9 } },
      { type: 'right_curve', length: 240, width: 16, radius: 95, banking: 8, lighting: { streetLights: true, neonIntensity: 1.0 } },
      { type: 'straight', length: 360, width: 16, lighting: { streetLights: true, neonIntensity: 0.9 } },
      { type: 'left_curve', length: 220, width: 16, radius: 90, banking: 7, lighting: { streetLights: true, neonIntensity: 1.0 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'clear',
      fogColor: 0x05060a,
      fogDensity: 0.012,
      ambientLight: 0.22,
      streetLightIntensity: 1.35,
      neonGlobalIntensity: 1.05
    },
    nfsFeatures: {
      hasDriftZones: false,
      hasSpeedCameras: true,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: false,
      soundtrackMood: 'intense'
    }
  },

  // === NEW: Tunnel Rush ===
  tunnel_rush: {
    id: 'tunnel_rush',
    name: 'Tunnel Rush',
    city: 'Stuttgart',
    description: 'Untergrund-Tunnel, harte Lichtwechsel, NFS-Atmosphäre pur',
    difficulty: 3,
    length: 4.2,
    avgWidth: 12,
    maxLapTime: 170000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 180), radius: 7, name: 'Tunnel 1', type: 'checkpoint' },
      { id: 'c2', position: V(-90, 0.05, 280), radius: 7, name: 'Tunnel Bend', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 7, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'tunnel', length: 220, width: 12, lighting: { streetLights: true, neonIntensity: 1.6, fogDensity: 0.03 } },
      { type: 'left_curve', length: 140, width: 12, radius: 45, lighting: { streetLights: true, neonIntensity: 1.5 } },
      { type: 'tunnel', length: 200, width: 12, lighting: { streetLights: true, neonIntensity: 1.6, fogDensity: 0.028 } },
      { type: 'straight', length: 160, width: 12, lighting: { streetLights: true, neonIntensity: 1.2 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'foggy',
      fogColor: 0x06070c,
      fogDensity: 0.026,
      ambientLight: 0.2,
      streetLightIntensity: 1.45,
      neonGlobalIntensity: 1.45
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: false,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: true,
      soundtrackMood: 'aggressive'
    }
  },

  // === NEW: Hills / Pass ===
  alpine_pass: {
    id: 'alpine_pass',
    name: 'Alpine Pass',
    city: 'Alps',
    description: 'Kurviger Pass, Sprünge, harte S-Kurven, stärkere Gegner',
    difficulty: 5,
    length: 5.8,
    avgWidth: 11,
    maxLapTime: 195000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.05, 0),
    finishPoint: V(0, 0.05, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.05, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 0.05, 220), radius: 7, name: 'Pass 1', type: 'checkpoint' },
      { id: 'c2', position: V(80, 0.05, 360), radius: 7, name: 'Hairpin', type: 'checkpoint' },
      { id: 'c3', position: V(-40, 0.05, 520), radius: 7, name: 'Jump Zone', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.05, 0), radius: 7, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 240, width: 11, lighting: { streetLights: false, neonIntensity: 0.2 } },
      { type: 'right_curve', length: 180, width: 11, radius: 62, lighting: { streetLights: false, neonIntensity: 0.2 } },
      { type: 'chicane', length: 150, width: 10, lighting: { streetLights: false, neonIntensity: 0.15 } },
      { type: 'hairpin', length: 120, width: 10, radius: 26, lighting: { streetLights: false, neonIntensity: 0.15 } },
      { type: 'jump', length: 120, width: 10, elevation: 3.0, lighting: { streetLights: false, neonIntensity: 0.15 } }
    ],
    environment: {
      timeOfDay: 'sunset',
      weather: 'clear',
      fogColor: 0x2b2230,
      fogDensity: 0.012,
      ambientLight: 0.55,
      streetLightIntensity: 0.5,
      neonGlobalIntensity: 0.5
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: false,
      hasPoliceChase: false,
      hasShortcuts: true,
      hasJumps: true,
      hasTunnels: false,
      soundtrackMood: 'intense'
    }
  },

  // === NEW CIRCUITS ===

  neon_downtown_circuit: {
    id: 'neon_downtown_circuit',
    name: 'Neon Downtown Circuit',
    city: 'Tokyo Metro',
    description: 'Geschlossener Downtown-Circuit mit Tunnel und Chicane',
    difficulty: 3,
    length: 5.1,
    avgWidth: 12,
    maxLapTime: 175000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 1, 0),
    finishPoint: V(0, 1, 0),
    checkpoints: [
      { id: 's', position: V(0, 1, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 1, 220), radius: 7, name: 'Straight End', type: 'checkpoint' },
      { id: 'c2', position: V(160, 1, 320), radius: 7, name: 'Right After', type: 'checkpoint' },
      { id: 'c3', position: V(160, 1, 540), radius: 7, name: 'Tunnel Exit', type: 'checkpoint' },
      { id: 'c4', position: V(60, 1, 640), radius: 7, name: 'Chicane Out', type: 'checkpoint' },
      { id: 'f', position: V(0, 1, 0), radius: 7, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 220, width: 12, lighting: { streetLights: true, neonIntensity: 1.3 } },
      { type: 'right_curve', length: 160, width: 12, radius: 55, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'tunnel', length: 220, width: 12, lighting: { streetLights: true, neonIntensity: 1.7, fogDensity: 0.032 } },
      { type: 'chicane', length: 160, width: 11, lighting: { streetLights: true, neonIntensity: 1.5 } },
      { type: 'left_curve', length: 170, width: 12, radius: 60, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'straight', length: 220, width: 12, lighting: { streetLights: true, neonIntensity: 1.3 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'clear',
      fogColor: 0x05060d,
      fogDensity: 0.02,
      ambientLight: 0.25,
      streetLightIntensity: 1.25,
      neonGlobalIntensity: 1.45
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: true,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: true,
      soundtrackMood: 'intense'
    }
  },

  industrial_ring: {
    id: 'industrial_ring',
    name: 'Industrial Ring',
    city: 'Berlin Outskirts',
    description: 'Industriering mit langen Geraden und Hairpins',
    difficulty: 3,
    length: 4.9,
    avgWidth: 13,
    maxLapTime: 172000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 1, 0),
    finishPoint: V(0, 1, 0),
    checkpoints: [
      { id: 's', position: V(0, 1, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(0, 1, 230), radius: 7, name: 'Factory Straight', type: 'checkpoint' },
      { id: 'c2', position: V(130, 1, 320), radius: 7, name: 'Hairpin 1', type: 'checkpoint' },
      { id: 'c3', position: V(200, 1, 520), radius: 7, name: 'Mid Straight', type: 'checkpoint' },
      { id: 'c4', position: V(70, 1, 620), radius: 7, name: 'Hairpin 2', type: 'checkpoint' },
      { id: 'f', position: V(0, 1, 0), radius: 7, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 240, width: 13, lighting: { streetLights: true, neonIntensity: 1.2 } },
      { type: 'hairpin', length: 110, width: 11, radius: 30, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'straight', length: 220, width: 13, lighting: { streetLights: true, neonIntensity: 1.2 } },
      { type: 'hairpin', length: 110, width: 11, radius: 30, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'straight', length: 200, width: 13, lighting: { streetLights: true, neonIntensity: 1.2 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'clear',
      fogColor: 0x04050a,
      fogDensity: 0.019,
      ambientLight: 0.22,
      streetLightIntensity: 1.3,
      neonGlobalIntensity: 1.35
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: true,
      hasPoliceChase: true,
      hasShortcuts: false,
      hasJumps: false,
      hasTunnels: false,
      soundtrackMood: 'aggressive'
    }
  },

  // === NEW SPRINTS ===

  coast_sprint: {
    id: 'coast_sprint',
    name: 'Coast Sprint',
    city: 'Miami',
    description: 'Point-to-point Küstenrennen mit Kurven und Sprung',
    difficulty: 2,
    length: 5.3,
    avgWidth: 14,
    maxLapTime: 160000,
    raceType: 'sprint' as RaceType,
    spawnPoint: V(0, 1, 0),
    finishPoint: V(1100, 1, -350),
    checkpoints: [
      { id: 's', position: V(0, 1, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(280, 1, -80), radius: 7, name: 'Beachfront', type: 'checkpoint' },
      { id: 'c2', position: V(600, 1, -120), radius: 7, name: 'Mid Coast', type: 'checkpoint' },
      { id: 'c3', position: V(900, 1, -250), radius: 7, name: 'Jump Zone', type: 'checkpoint' },
      { id: 'f', position: V(1100, 1, -350), radius: 8, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 300, width: 14, lighting: { streetLights: false, neonIntensity: 0.4 } },
      { type: 'left_curve', length: 180, width: 14, radius: 65, lighting: { streetLights: false, neonIntensity: 0.4 } },
      { type: 'straight', length: 320, width: 14, lighting: { streetLights: false, neonIntensity: 0.35 } },
      { type: 'right_curve', length: 170, width: 14, radius: 70, lighting: { streetLights: false, neonIntensity: 0.4 } },
      { type: 'jump', length: 140, width: 13, elevation: 2.5, lighting: { streetLights: false, neonIntensity: 0.3 } }
    ],
    environment: {
      timeOfDay: 'day',
      weather: 'clear',
      fogColor: 0xa8d2ff,
      fogDensity: 0.005,
      ambientLight: 0.75,
      streetLightIntensity: 0.3,
      neonGlobalIntensity: 0.45
    },
    nfsFeatures: {
      hasDriftZones: false,
      hasSpeedCameras: true,
      hasPoliceChase: false,
      hasShortcuts: true,
      hasJumps: true,
      hasTunnels: false,
      soundtrackMood: 'chill'
    }
  },

  downtown_sprint: {
    id: 'downtown_sprint',
    name: 'Downtown Sprint',
    city: 'Stuttgart',
    description: 'Schneller Point-to-point durch die Innenstadt',
    difficulty: 3,
    length: 4.3,
    avgWidth: 11,
    maxLapTime: 155000,
    raceType: 'sprint' as RaceType,
    spawnPoint: V(0, 1, 0),
    finishPoint: V(950, 1, -280),
    checkpoints: [
      { id: 's', position: V(0, 1, 0), radius: 7, name: 'Start', type: 'start' },
      { id: 'c1', position: V(240, 1, -90), radius: 7, name: 'City Center', type: 'checkpoint' },
      { id: 'c2', position: V(550, 1, -150), radius: 7, name: 'Mid Sprint', type: 'checkpoint' },
      { id: 'c3', position: V(780, 1, -220), radius: 7, name: 'Final Turn', type: 'checkpoint' },
      { id: 'f', position: V(950, 1, -280), radius: 8, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 260, width: 11, lighting: { streetLights: true, neonIntensity: 1.3 } },
      { type: 'right_curve', length: 140, width: 11, radius: 50, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'chicane', length: 180, width: 10, lighting: { streetLights: true, neonIntensity: 1.5 } },
      { type: 'straight', length: 240, width: 11, lighting: { streetLights: true, neonIntensity: 1.3 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'clear',
      fogColor: 0x06070c,
      fogDensity: 0.018,
      ambientLight: 0.25,
      streetLightIntensity: 1.3,
      neonGlobalIntensity: 1.4
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: true,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: false,
      soundtrackMood: 'intense'
    }
  },

  // === NEW: Moscow Racing Map ===
  moscow_racing: {
    id: 'moscow_racing',
    name: 'Moscow Street Circuit',
    city: 'Moscow',
    description: 'Hochgeschwindigkeits-Rennen auf echter Moskauer Rennstrecke',
    difficulty: 4,
    length: 6.5,
    avgWidth: 14,
    maxLapTime: 220000,
    raceType: 'circuit' as RaceType,
    laps: 3,
    spawnPoint: V(0, 0.5, 0),
    finishPoint: V(0, 0.5, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.5, 0), radius: 10, name: 'Start', type: 'start' },
      { id: 'c1', position: V(300, 0.5, -150), radius: 12, name: 'Turn 1', type: 'checkpoint' },
      { id: 'c2', position: V(450, 0.5, -400), radius: 12, name: 'Moscow Bend', type: 'checkpoint' },
      { id: 'c3', position: V(200, 0.5, -600), radius: 12, name: 'Red Square', type: 'checkpoint' },
      { id: 'c4', position: V(-100, 0.5, -450), radius: 12, name: 'River Turn', type: 'checkpoint' },
      { id: 'c5', position: V(-150, 0.5, -150), radius: 12, name: 'Final Straight', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.5, 0), radius: 10, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 350, width: 14, lighting: { streetLights: true, neonIntensity: 1.1 } },
      { type: 'right_curve', length: 200, width: 14, radius: 65, lighting: { streetLights: true, neonIntensity: 1.2 } },
      { type: 'straight', length: 280, width: 14, lighting: { streetLights: true, neonIntensity: 1.0 } },
      { type: 'left_curve', length: 180, width: 13, radius: 55, lighting: { streetLights: true, neonIntensity: 1.3 } },
      { type: 'chicane', length: 220, width: 12, lighting: { streetLights: true, neonIntensity: 1.2 } },
      { type: 'straight', length: 300, width: 14, lighting: { streetLights: true, neonIntensity: 1.1 } }
    ],
    environment: {
      timeOfDay: 'sunset',
      weather: 'clear',
      fogColor: 0xff8844,
      fogDensity: 0.008,
      ambientLight: 0.55,
      streetLightIntensity: 0.8,
      neonGlobalIntensity: 0.9
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: true,
      hasPoliceChase: false,
      hasShortcuts: true,
      hasJumps: false,
      hasTunnels: false,
      soundtrackMood: 'intense'
    },
    environmentMapPath: '/assets/car-race/cars/sports_car_racing_moscow.glb',
    environmentMapScale: 100.0
  },

  // === NEW: NFS Underground 2 City ===
  nfs_u2_city: {
    id: 'nfs_u2_city',
    name: 'Underground City Circuit',
    city: 'Bayview',
    description: 'Klassisches NFS U2 Feeling - urbaner Circuit durch die ganze Stadt',
    difficulty: 5,
    length: 8.2,
    avgWidth: 13,
    maxLapTime: 280000,
    raceType: 'circuit' as RaceType,
    laps: 2,
    spawnPoint: V(0, 0.5, 0),
    finishPoint: V(0, 0.5, 0),
    checkpoints: [
      { id: 's', position: V(0, 0.5, 0), radius: 12, name: 'Start', type: 'start' },
      { id: 'c1', position: V(400, 0.5, -200), radius: 14, name: 'Downtown', type: 'checkpoint' },
      { id: 'c2', position: V(650, 0.5, -500), radius: 14, name: 'Harbor', type: 'checkpoint' },
      { id: 'c3', position: V(500, 0.5, -800), radius: 14, name: 'Industrial', type: 'checkpoint' },
      { id: 'c4', position: V(100, 0.5, -950), radius: 14, name: 'Bay Bridge', type: 'checkpoint' },
      { id: 'c5', position: V(-300, 0.5, -750), radius: 14, name: 'Stadium', type: 'checkpoint' },
      { id: 'c6', position: V(-400, 0.5, -400), radius: 14, name: 'Airport', type: 'checkpoint' },
      { id: 'c7', position: V(-200, 0.5, -100), radius: 14, name: 'Suburbs', type: 'checkpoint' },
      { id: 'f', position: V(0, 0.5, 0), radius: 12, name: 'Finish', type: 'finish' }
    ],
    segments: [
      { type: 'straight', length: 420, width: 13, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'right_curve', length: 240, width: 13, radius: 60, lighting: { streetLights: true, neonIntensity: 1.5 } },
      { type: 'straight', length: 380, width: 13, lighting: { streetLights: true, neonIntensity: 1.3 } },
      { type: 'left_curve', length: 220, width: 12, radius: 50, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'tunnel', length: 280, width: 13, lighting: { streetLights: true, neonIntensity: 1.6 } },
      { type: 'chicane', length: 250, width: 12, lighting: { streetLights: true, neonIntensity: 1.5 } },
      { type: 'straight', length: 350, width: 13, lighting: { streetLights: true, neonIntensity: 1.4 } },
      { type: 'hairpin', length: 180, width: 11, radius: 35, lighting: { streetLights: true, neonIntensity: 1.6 } },
      { type: 'straight', length: 400, width: 13, lighting: { streetLights: true, neonIntensity: 1.3 } }
    ],
    environment: {
      timeOfDay: 'night',
      weather: 'clear',
      fogColor: 0x0a0b14,
      fogDensity: 0.012,
      ambientLight: 0.30,
      streetLightIntensity: 1.4,
      neonGlobalIntensity: 1.5
    },
    nfsFeatures: {
      hasDriftZones: true,
      hasSpeedCameras: true,
      hasPoliceChase: true,
      hasShortcuts: true,
      hasJumps: true,
      hasTunnels: true,
      soundtrackMood: 'intense'
    },
    environmentMapPath: '/assets/car-race/maps/source/u2bw1.glb',
    environmentMapScale: 12.0
  }
};

export function listUrbanTracks(): UrbanTrackDefinition[] {
  return Object.values(URBAN_TRACK_CATALOG);
}

export function getUrbanTrackById(id: string): UrbanTrackDefinition | null {
  return URBAN_TRACK_CATALOG[id] ?? null;
}

/**
 * Generiere Minimap-Punkte aus Track-Geometrie
 * Simuliert die Streckenform basierend auf Segmenten
 */
export function getTrackMiniMapPoints(track: UrbanTrackDefinition): THREE.Vector2[] {
  const points: THREE.Vector2[] = [];
  let x = 0;
  let z = 0;
  let yaw = 0; // Blickrichtung in Radiant

  points.push(new THREE.Vector2(x, z));

  for (const seg of track.segments) {
    const len = seg.length;
    const stepCount = Math.max(4, Math.floor(len / 40));

    switch (seg.type) {
      case 'straight':
      case 'tunnel':
      case 'jump':
        for (let i = 1; i <= stepCount; i++) {
          const dist = (len / stepCount) * i;
          x += Math.sin(yaw) * dist / stepCount;
          z += Math.cos(yaw) * dist / stepCount;
          points.push(new THREE.Vector2(x, z));
        }
        break;

      case 'left_curve':
      case 'right_curve':
        const radius = seg.radius ?? 50;
        const angleChange = len / radius;
        const direction = seg.type === 'left_curve' ? -1 : 1;
        
        for (let i = 1; i <= stepCount; i++) {
          yaw += (angleChange / stepCount) * direction;
          const dist = len / stepCount;
          x += Math.sin(yaw) * dist;
          z += Math.cos(yaw) * dist;
          points.push(new THREE.Vector2(x, z));
        }
        break;

      case 'hairpin':
        const turn = Math.PI * 0.85; // 180° turn
        const turnDir = seg.radius && seg.radius < 35 ? 1 : -1;
        for (let i = 1; i <= stepCount; i++) {
          yaw += (turn / stepCount) * turnDir;
          const dist = len / stepCount;
          x += Math.sin(yaw) * dist;
          z += Math.cos(yaw) * dist;
          points.push(new THREE.Vector2(x, z));
        }
        break;

      case 'chicane':
        // S-Kurve: links dann rechts
        const halfSteps = Math.floor(stepCount / 2);
        for (let i = 1; i <= halfSteps; i++) {
          yaw -= 0.55 / halfSteps;
          x += Math.sin(yaw) * (len / stepCount);
          z += Math.cos(yaw) * (len / stepCount);
          points.push(new THREE.Vector2(x, z));
        }
        for (let i = halfSteps + 1; i <= stepCount; i++) {
          yaw += 0.55 / (stepCount - halfSteps);
          x += Math.sin(yaw) * (len / stepCount);
          z += Math.cos(yaw) * (len / stepCount);
          points.push(new THREE.Vector2(x, z));
        }
        break;
    }
  }

  if (track.raceType === 'circuit' && points.length > 1) {
    const first = points[0];
    const last = points[points.length - 1];
    const dx = last.x - first.x;
    const dz = last.y - first.y;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist > 1) {
      points.push(new THREE.Vector2(first.x, first.y));
    }
  }

  return points;
}
